import moment from "moment";
import { InvoiceApi } from "@/api/InvoiceApi";
import { InvoicePaymentApi } from "@/api/InvoicePaymentApi";
import { InvoiceProductApi } from "@/api/InvoiceProductApi";
import { ClientApi } from "@/api/ClientApi";
import { EDOCUMENT_SUBMITION_STATUS_CODE, EDOCUMENT_SUBMITION_STATUS_SENT } from "@/atv/eInvoiceApi";
import {
    CURRENCIES_CODE_IDS,
    EINVOICE_INVOICE_TYPE_CODE,
    EINVOICE_TICKET_TYPE_CODE,
    INVOICE_CONTADO_TYPE_CODE,
    INVOICE_CREDIT_TYPE_CODE,
} from "@utils/Constants";
import { resolveDocumentClientNames } from "@utils/documentClientSnapshot";
import { formatMoneyValue } from "@utils/Currency";
import { CASH_SALE_CONDITION } from "../core/resolveEDocuments";
import { paymentWayFromAtvCode, paymentWayLabel } from "../core/paymentWays";
import { serializeParsedReferences } from "../core/storedReferences";
import { serializeParsedOtherCharges } from "../core/storedOtherCharges";
import {
    EDocumentImportDefinition,
    ParsedEDocument,
    ResolvedReferences,
} from "../core/types";

export const INVOICE_ROOT_NAME = "FacturaElectronica";
export const TICKET_ROOT_NAME = "TiqueteElectronico";

/** Largo de sucursal + terminal + tipo dentro del consecutivo de 20 digitos. */
const SEQUENCE_PREFIX_LENGTH = 10;

/**
 * Estado con el que quedan los comprobantes cargados: ya fueron aceptados por Hacienda en el
 * sistema anterior, asi que la carga los marca como enviados y aceptados sin volver a
 * emitirlos. Reenviarlos seria un rechazo seguro por clave duplicada.
 */
export const IMPORTED_ATV_STATUS_PAYLOAD = JSON.stringify({ status: "aceptado" });

/**
 * Nombre de respaldo cuando el comprobante no trae receptor (el caso normal del tiquete
 * electronico). `Invoice.nombre_factura` es requerido, asi que no puede quedar vacio.
 */
export const NO_RECEIVER_NAME = "Cliente contado";

const isCashSale = (parsed: ParsedEDocument): boolean =>
    `${parsed.condicionVenta}`.padStart(2, "0") === CASH_SALE_CONDITION;

/** La parte consecutiva del `NumeroConsecutivo`, que es lo que el modelo guarda en `numero`. */
export const invoiceNumberFromSequence = (sequence: string): number =>
    Number(`${sequence || ""}`.slice(SEQUENCE_PREFIX_LENGTH)) || 0;

const buildHeader = (parsed: ParsedEDocument, refs: ResolvedReferences): Record<string, unknown> => {
    const cash = isCashSale(parsed);
    const receptor = parsed.receptor;
    const { nombre_factura, razon_social } = resolveDocumentClientNames({
        nombre: receptor?.nombre || "",
        nombre_comercial: receptor?.nombreComercial || "",
    });

    return {
        invoice_key: parsed.clave,
        sequence: parsed.numeroConsecutivo,
        numero: invoiceNumberFromSequence(parsed.numeroConsecutivo),
        // `parseZone` conserva la fecha con la que se emitio el comprobante. Con `moment()` se
        // moveria a la zona de la maquina que hace la carga, y una factura emitida de noche
        // podria quedar registrada al dia siguiente.
        fecha: moment.parseZone(parsed.fechaEmision).format("YYYY-MM-DD"),
        tipo: cash ? INVOICE_CONTADO_TYPE_CODE : INVOICE_CREDIT_TYPE_CODE,
        saleCondition: Number(parsed.condicionVenta) || undefined,
        plazo: Number(parsed.plazoCredito) || 0,
        einvoice_kind:
            parsed.rootName === TICKET_ROOT_NAME ? EINVOICE_TICKET_TYPE_CODE : EINVOICE_INVOICE_TYPE_CODE,
        einvoice_status: EDOCUMENT_SUBMITION_STATUS_CODE[EDOCUMENT_SUBMITION_STATUS_SENT],
        edocument_status_payload: IMPORTED_ATV_STATUS_PAYLOAD,
        status: cash ? "pagada" : "pendiente",
        pagada: cash,
        cerrada: true,
        anulada: false,

        // Snapshot del receptor: el documento guarda los datos con los que se emitio, aunque
        // el maestro del cliente cambie despues.
        clientId: refs.clientId,
        nombre_factura: nombre_factura || NO_RECEIVER_NAME,
        razon_social: razon_social || NO_RECEIVER_NAME,
        tipo_identificacion: receptor?.identificacion.tipo,
        identificacion: receptor?.identificacion.numero,
        email: receptor?.correo,
        tel1_cd: receptor?.telefonoCodigoPais,
        tel1: receptor?.telefono,
        direccion: receptor?.direccion,
        clientActivitycode: parsed.codigoActividadReceptor,
        with_user_info: !!receptor?.identificacion.numero,

        activitycode: parsed.codigoActividadEmisor,
        currencyId: refs.currencyId,
        exchange_rate: refs.exchangeRate,

        subtotal: parsed.resumen.totalVenta,
        descuento: parsed.resumen.totalDescuentos,
        impuestos: parsed.resumen.totalImpuesto,
        impuestos_servicio: 0,
        total: parsed.resumen.totalComprobante,

        // Con la forma de `StoredOtherCharge`, no la del parser: `parseStoredOtherCharges`
        // descarta toda entrada que no la tenga y el documento se imprime sin cargos.
        otros_cargos: serializeParsedOtherCharges(parsed),
        // Con la forma de `StoredReference`, no la del parser: el reenvio a Hacienda descarta
        // en silencio toda referencia que no traiga `fechaEmisionUTC`.
        info_references: serializeParsedReferences(parsed),
    };
};

/**
 * Pagos del comprobante.
 *
 * Solo los de contado los generan: una factura a credito entra pendiente para que aparezca en
 * cuentas por cobrar con su plazo, que es justo el saldo que el tenant necesita conservar al
 * migrar. Cuando el XML declara varios medios sin desglose (`TotalMedioPago` es opcional), el
 * total se reparte en el primero para no inventar montos.
 */
const buildPayments = (
    parsed: ParsedEDocument,
    refs: ResolvedReferences
): Record<string, unknown>[] => {
    if (!isCashSale(parsed)) return [];

    const total = parsed.resumen.totalComprobante;
    const medios = parsed.mediosPago.length ? parsed.mediosPago : [{ codigo: "01", otros: "" }];
    const declaredTotal = medios.reduce((sum, medio) => sum + (medio.total || 0), 0);

    return medios.map((medio, index) => {
        const formaPago = paymentWayFromAtvCode(medio.codigo);
        const monto = declaredTotal > 0 ? medio.total || 0 : index === 0 ? total : 0;

        return {
            descripcion: medio.otros || paymentWayLabel(formaPago),
            formaPago: `${formaPago}`,
            monto,
            vuelto: 0,
            exchangeRate: refs.exchangeRate,
            currencyId: refs.currencyId,
            created: moment.parseZone(parsed.fechaEmision).format("YYYY-MM-DD HH:mm:ss"),
        };
    });
};

/** Todo lo que el asistente de carga necesita saber de las facturas y tiquetes electronicos. */
export const invoiceImportDefinition: EDocumentImportDefinition = {
    documentType: "Invoice",
    acceptedRootNames: [INVOICE_ROOT_NAME, TICKET_ROOT_NAME],

    ui: {
        title: "Cargar Facturas XML",
        backUrl: "/user/invoices",
        doneActionTitle: "Ir a la lista de facturas",
        entityPlural: "facturas",
        fileHelpText:
            "Seleccione el XML de una factura o tiquete electronico, o un archivo ZIP con varios. Los comprobantes se cargan tal como fueron emitidos: conservan su consecutivo y su clave de Hacienda, y no se vuelven a enviar.",
        reviewHelpText:
            "Revise a que cliente y a que productos quedo ligado cada comprobante. Los productos que no existan en el inventario se guardan como productos temporales, con la informacion que trae el XML.",
        confirmHelpText:
            "La carga no modifica existencias ni consume consecutivos: son documentos ya emitidos.",
        doneHelpText:
            "El listado de facturas se alimenta del buscador, que se actualiza unos segundos despues de la carga.",
    },

    loadExistingKeys: () => InvoiceApi.fetchAllDocumentKeys(),

    buildHeader,
    buildPayments,

    previewColumns: [
        { header: "Consecutivo", render: (row) => row.parsed.numeroConsecutivo },
        {
            header: "Fecha",
            render: (row) =>
                row.parsed.fechaEmision ? moment(row.parsed.fechaEmision).format("DD-MM-YYYY") : "",
        },
        { header: "Receptor", render: (row) => row.parsed.receptor?.nombre || "Sin receptor" },
        {
            header: "Total",
            render: (row) =>
                formatMoneyValue(
                    row.parsed.resumen.totalComprobante,
                    CURRENCIES_CODE_IDS[row.parsed.resumen.codigoMoneda]
                ),
        },
    ],

    invalidateCaches: async () => {
        await InvoiceApi.invalidateAllByTenant();
        InvoiceApi.clearCache();
        await InvoiceProductApi.invalidateAllByTenant();
        await InvoicePaymentApi.invalidateAllByTenant();
        await ClientApi.invalidateAllByTenant();
        ClientApi.clearCache();
    },
};

export default invoiceImportDefinition;
